/**
 * Admin Tenant Management API
 * Handles tenant listing, user management, and impersonation
 *
 * Created: July 9, 2025
 * Purpose: Admin portal functionality for managing tenants and users
 */

import AdminAuthAPI from './auth.js';
import enhancedTokenManager from '../../utils/enhancedTokenManager';
import { getApiBaseUrl } from '../../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

export class AdminTenantAPI {
  
  /**
   * Get all tenants with pagination and filtering
   * @param {object} params - Query parameters
   * @returns {Promise<{tenants: Array, total: number, pagination: object}>}
   */
  static async getTenants(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 25,
        search: params.search || '',
        status: params.status || '',
        migration_status: params.migration_status || '',
        sort_by: params.sort_by || 'created_at',
        sort_order: params.sort_order || 'desc'
      }).toString();

      const response = await AdminAuthAPI.authenticatedRequest(
        `/admin/tenants?${queryParams}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch tenants');
      }

      return await response.json();
    } catch (error) {
      console.error('Get tenants error:', error);
      throw error;
    }
  }

  /**
   * Get specific tenant details
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<object>} Tenant details
   */
  static async getTenant(tenantId) {
    try {
      const response = await AdminAuthAPI.authenticatedRequest(
        `/admin/tenants/${tenantId}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch tenant details');
      }

      return await response.json();
    } catch (error) {
      console.error('Get tenant error:', error);
      throw error;
    }
  }

  /**
   * Get detailed statistics for a specific tenant
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<object>} Tenant stats with record counts
   */
  static async getTenantStats(tenantId) {
    try {
      const response = await AdminAuthAPI.authenticatedRequest(`/admin/tenants/${tenantId}/stats`);
      if (!response.ok) {
        throw new Error('Failed to fetch tenant statistics');
      }
      return await response.json();
    } catch (error) {
      console.error('Get tenant stats error:', error);
      throw error;
    }
  }

  /**
   * Get users for a specific tenant
   * @param {string} tenantId - Tenant ID
   * @param {object} params - Query parameters
   * @returns {Promise<{users: Array, total: number}>}
   */
  static async getTenantUsers(tenantId, params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 25,
        search: params.search || '',
        role: params.role || '',
        auth_type: params.auth_type || '' // legacy or stytch
      }).toString();

      const response = await AdminAuthAPI.authenticatedRequest(
        `/admin/tenants/${tenantId}/users?${queryParams}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch tenant users');
      }

      return await response.json();
    } catch (error) {
      console.error('Get tenant users error:', error);
      throw error;
    }
  }

  /**
   * Get tenant migration status
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<object>} Migration status details
   */
  static async getTenantMigrationStatus(tenantId) {
    try {
      const response = await AdminAuthAPI.authenticatedRequest(
        `/admin/tenants/${tenantId}/migration-status`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch migration status');
      }

      return await response.json();
    } catch (error) {
      console.error('Get migration status error:', error);
      throw error;
    }
  }

  /**
   * Start impersonation of a user
   * @param {string} userId - User ID to impersonate
   * @param {string} reason - Reason for impersonation
   * @returns {Promise<{impersonation_token: string, user: object}>}
   */
  static async startImpersonation(userId, reason) {
    try {
      const response = await AdminAuthAPI.authenticatedRequest(
        '/admin/impersonation/start',
        {
          method: 'POST',
          body: JSON.stringify({
            target_user_id: userId,
            reason: reason
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to start impersonation');
      }

      const data = await response.json();
      
      // Store impersonation session info for admin portal
      localStorage.setItem('impersonation_token', data.impersonation_token);
      localStorage.setItem('impersonation_user', JSON.stringify(data.impersonated_user));
      localStorage.setItem('impersonation_started_at', new Date().toISOString());
      
      // CRITICAL: Also set as main auth_token so the main app recognizes the impersonated user
      // First backup the original admin token
      const adminToken = AdminAuthAPI.getToken();
      if (adminToken) {
        localStorage.setItem('original_admin_token', adminToken);
      }

      // Set the impersonation token as the main auth token (secure storage + legacy localStorage)
      await enhancedTokenManager.setToken(data.impersonation_token, 'auth_token');

      return data;
    } catch (error) {
      console.error('Start impersonation error:', error);
      throw error;
    }
  }

  /**
   * Stop current impersonation session
   * @returns {Promise<void>}
   */
  static async stopImpersonation() {
    try {
      const impersonationToken = localStorage.getItem('impersonation_token');
      const response = await AdminAuthAPI.authenticatedRequest(
        '/admin/impersonation/stop',
        {
          method: 'POST',
          body: JSON.stringify({
            impersonation_token: impersonationToken
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to stop impersonation');
      }

      // Clear impersonation session data
      localStorage.removeItem('impersonation_token');
      localStorage.removeItem('impersonation_user');
      localStorage.removeItem('impersonation_started_at');
      
      // CRITICAL: Restore the original admin token
      const originalAdminToken = localStorage.getItem('original_admin_token');
      if (originalAdminToken) {
        localStorage.setItem('admin_token', originalAdminToken);
        // Do NOT reuse the admin token as the user auth token; clear user context
        await enhancedTokenManager.clearToken('auth_token');
        localStorage.removeItem('original_admin_token');
      } else {
        // Fallback: clear auth_token if no backup found
        await enhancedTokenManager.clearToken('auth_token');
      }

    } catch (error) {
      console.error('Stop impersonation error:', error);
      throw error;
    }
  }

  /**
   * Get current impersonation status
   * @returns {object|null} Impersonation session info
   */
  static getImpersonationStatus() {
    const token = localStorage.getItem('impersonation_token');
    const user = localStorage.getItem('impersonation_user');
    const startedAt = localStorage.getItem('impersonation_started_at');

    // Check if any required data is missing or null
    if (!token || !user || user === 'undefined' || user === 'null') {
      // Clear all impersonation data if any part is missing/corrupted
      localStorage.removeItem('impersonation_token');
      localStorage.removeItem('impersonation_user');
      localStorage.removeItem('impersonation_started_at');
      return null;
    }

    try {
      const parsedUser = JSON.parse(user);
      return {
        token,
        user: parsedUser,
        started_at: startedAt,
        is_impersonating: true
      };
    } catch (error) {
      console.error('Error parsing impersonation user data:', error);
      // Clear corrupted data
      localStorage.removeItem('impersonation_token');
      localStorage.removeItem('impersonation_user');
      localStorage.removeItem('impersonation_started_at');
      return null;
    }
  }

  /**
   * Get impersonation status from server
   * @returns {Promise<object>} Server-side impersonation status
   */
  static async getImpersonationStatusFromServer() {
    try {
      const impersonationToken = localStorage.getItem('impersonation_token');
      const response = await AdminAuthAPI.authenticatedRequest('/admin/impersonation/status', {
        method: 'POST',
        body: JSON.stringify({
          impersonation_token: impersonationToken
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch impersonation status');
      }

      return await response.json();
    } catch (error) {
      console.error('Get impersonation status from server error:', error);
      throw error;
    }
  }

  /**
   * Get impersonation logs for audit trail
   * @param {object} params - Query parameters
   * @returns {Promise<{logs: Array, total: number}>}
   */
  static async getImpersonationLogs(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 25,
        admin_user_id: params.admin_user_id || '',
        target_user_id: params.target_user_id || '',
        start_date: params.start_date || '',
        end_date: params.end_date || ''
      }).toString();

      const response = await AdminAuthAPI.authenticatedRequest(
        `/admin/impersonation/logs?${queryParams}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch impersonation logs');
      }

      return await response.json();
    } catch (error) {
      console.error('Get impersonation logs error:', error);
      throw error;
    }
  }

  /**
   * Start migration for a tenant
   * @param {string} tenantId - Tenant ID
   * @param {string} migrationType - Type of migration
   * @returns {Promise<object>} Migration status
   */
  static async startMigration(tenantId, migrationType = 'auth_to_stytch') {
    try {
      const response = await AdminAuthAPI.authenticatedRequest(
        `/admin/tenants/${tenantId}/migration/start`,
        {
          method: 'POST',
          body: JSON.stringify({
            migration_type: migrationType
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to start migration');
      }

      return await response.json();
    } catch (error) {
      console.error('Start migration error:', error);
      throw error;
    }
  }

  /**
   * Get system overview statistics
   * @returns {Promise<object>} System statistics
   */
  static async getSystemOverview() {
    try {
      const response = await AdminAuthAPI.authenticatedRequest(
        '/admin/system/overview'
      );

      if (!response.ok) {
        throw new Error('Failed to fetch system overview');
      }

      return await response.json();
    } catch (error) {
      console.error('Get system overview error:', error);
      throw error;
    }
  }

  /**
   * Get admin activity logs
   * @param {object} params - Query parameters
   * @returns {Promise<{logs: Array, total: number}>}
   */
  static async getAdminActivityLogs(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 25,
        admin_user_id: params.admin_user_id || '',
        action_type: params.action_type || '',
        start_date: params.start_date || '',
        end_date: params.end_date || ''
      }).toString();

      const response = await AdminAuthAPI.authenticatedRequest(
        `/admin/activity/logs?${queryParams}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch activity logs');
      }

      return await response.json();
    } catch (error) {
      console.error('Get activity logs error:', error);
      throw error;
    }
  }

  /**
   * Update tenant settings
   * @param {string} tenantId - Tenant ID
   * @param {object} settings - Settings to update
   * @returns {Promise<object>} Updated tenant
   */
  static async updateTenantSettings(tenantId, settings) {
    try {
      const response = await AdminAuthAPI.authenticatedRequest(
        `/admin/tenants/${tenantId}/settings`,
        {
          method: 'PUT',
          body: JSON.stringify(settings)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update tenant settings');
      }

      return await response.json();
    } catch (error) {
      console.error('Update tenant settings error:', error);
      throw error;
    }
  }
}

export default AdminTenantAPI; 
